import RolePreview, { RolePreviewBanner } from "@/components/RolePreview";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LanguageContext";
import { useRefresh } from "@/context/RefreshContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Settings, LogOut, Moon, Sun, Menu, X, Map, Database, RefreshCw, Monitor, BellRing,
} from "lucide-react";
import { portalAssetUrl } from "@/lib/assets";
import { canViewRawData, isAdmin, isDemo, roleLabel } from "@/lib/permissions";

export function Brand({ compact = false }) {
  return (
    <div className="flex min-w-0 flex-col items-start text-white">
      <img
        src={portalAssetUrl("total-hydraulics-logo.svg")}
        alt="Total Hydraulics"
        className={`${compact ? "h-9 w-auto max-w-[156px]" : "h-12 w-auto max-w-[190px]"} shrink-0 object-contain object-left`}
      />
      <div className={`${compact ? "mt-0.5 pl-[43px] text-[8px]" : "mt-1 pl-[58px] text-[9px]"} font-black leading-none tracking-[0.3em] text-white/90`}>THRM</div>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, lang, toggle: toggleLang } = useLang();
  const { autoRefresh, setAutoRefresh, refreshNow } = useRefresh();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/", label: t("Dashboard", "Dashboard"), icon: LayoutDashboard, end: true },
    ...(!isDemo(user) ? [{ to: "/kaart", label: t("Kaart", "Map"), icon: Map }] : []),
    { to: "/tv", label: t("Tv-overzicht", "TV overview"), icon: Monitor },
    { to: "/alarms", label: t("Alarminstellingen", "Alarm settings"), icon: BellRing },
  ];
  if (isAdmin(user)) {
    nav.push({ to: "/admin", label: t("Beheer", "Admin"), icon: Settings });
  }
  if (canViewRawData(user)) {
    nav.push({ to: "/raw", label: t("Ruwe data", "Raw data"), icon: Database });
  }

  const NavItems = () => (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setOpen(false)}
          data-testid={`nav-${n.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-24 items-center border-b border-border bg-[#465259] px-4">
          <Brand />
        </div>
        <div className="flex-1 py-5"><NavItems /></div>
        <div className="border-t border-border p-3">
          <div className="mb-2 rounded-md bg-accent/50 px-3 py-2">
            <div className="truncate text-sm font-medium">{user?.name}</div>
            <div className="truncate text-xs text-muted-foreground">{user?.organization?.name || (user?.role === "admin" ? t("Systeembeheerder", "System administrator") : "—")}</div>
            <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{roleLabel(user?.role, t)}</div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> {t("Uitloggen", "Sign out")}
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-border bg-card">
            <div className="flex h-20 items-center justify-between border-b border-border bg-[#465259] px-5">
              <Brand compact />
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => setOpen(false)}><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 py-5"><NavItems /></div>
            <div className="border-t border-border p-3">
              <Button variant="ghost" size="sm" className="w-full justify-start gap-2" onClick={async () => { await logout(); navigate("/login"); }}>
                <LogOut className="h-4 w-4" /> {t("Uitloggen", "Sign out")}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex min-h-16 flex-wrap items-center justify-between gap-y-2 py-2 border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden lg:block" />
          <div className="ml-auto flex flex-wrap justify-end items-center gap-2">
            <RolePreview />
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5" data-testid="global-refresh">
              <button onClick={refreshNow} title={t("Nu vernieuwen", "Refresh now")} data-testid="refresh-now-btn"
                className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <RefreshCw className="h-4 w-4" />
              </button>
              <button onClick={() => setAutoRefresh(!autoRefresh)} title={t("Automatisch verversen", "Auto refresh")} data-testid="auto-refresh-toggle"
                className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold transition-colors ${
                  autoRefresh ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${autoRefresh ? "bg-primary animate-pulse" : "bg-slate-400"}`} />
                Auto
              </button>
            </div>
            <Button variant="outline" size="sm" className="h-9 px-3 font-mono text-xs font-bold" onClick={toggleLang} data-testid="lang-toggle" aria-label={t("Taal wisselen", "Change language")}>
              {lang === "nl" ? "NL" : "EN"}
            </Button>
            <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label={t("Thema wisselen", "Change theme")}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <RolePreviewBanner />
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
      <img src={portalAssetUrl("favicon.png")} alt="" aria-hidden="true" className="pointer-events-none fixed bottom-3 right-3 z-20 h-7 w-7 rounded-lg opacity-55 shadow-sm transition-opacity hover:opacity-80 sm:bottom-4 sm:right-4" />
    </div>
  );
}
